import {memo, useEffect, useState} from "react";
import {Tag} from "antd";
import dayjs from "dayjs";
import 'dayjs/locale/vi';
import {Link} from "react-router-dom";
import {handleRequest, apiError} from "~/utils";
import {trainingScheduleApi} from "~/api";
import FontAwesomeIcon from "~/components/FontAwesome";
import DashboardWidgetHeader from "./DashboardWidgetHeader";

dayjs.locale('vi');

const STATUS_MAP = {
	upcoming:    {label: 'Sắp diễn ra',  color: 'processing'},
	in_progress: {label: 'Đang diễn ra', color: 'blue'},
	overdue:     {label: 'Quá giờ',      color: 'orange'},
	trained:     {label: 'Đã đào tạo',   color: 'success'},
	not_trained: {label: 'Chưa đào tạo', color: 'error'},
	postponed:   {label: 'Đời lịch',     color: 'warning'},
	cancelled:   {label: 'Đã hủy',       color: 'default'},
};

const DOT_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444'];

function getWeekRange() {
	const now  = dayjs();
	const dow  = now.day() === 0 ? 7 : now.day();
	const mon  = now.subtract(dow - 1, 'day');
	const sun  = mon.add(6, 'day');
	return `${mon.format('DD/MM')} – ${sun.format('DD/MM/YYYY')}`;
}

function TrainingScheduleWeek() {

	const [items, setItems]     = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		setLoading(true);
		handleRequest(trainingScheduleApi.week()).then(([err, res]) => {
			if (!apiError('', err, res) && res?.data) {
				const today = dayjs();
				const sorted = [...res.data].sort((a, b) => {
					const aToday = a.startTime && dayjs.unix(a.startTime).isSame(today, 'day');
					const bToday = b.startTime && dayjs.unix(b.startTime).isSame(today, 'day');
					if (aToday && !bToday) return -1;
					if (!aToday && bToday) return 1;
					return (a.startTime || 0) - (b.startTime || 0);
				});
				setItems(sorted);
			}
			setLoading(false);
		});
	}, []);

	const viewAllLink = (
		<Link to="/training-schedule" style={{
			color: '#fff', fontSize: 11, opacity: 0.85,
			display: 'flex', alignItems: 'center', gap: 3, textDecoration: 'none',
		}}>
			Xem tất cả <FontAwesomeIcon icon="fa-light fa-arrow-right" />
		</Link>
	);

	return (
		<div style={{borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(99,102,241,0.12)',background: '#fff'}}>
			<DashboardWidgetHeader
				icon="fa-light fa-calendar-check"
				title="Lịch đào tạo trong tuần"
				subtitle={getWeekRange()}
				badge={items.length > 0 ? `${items.length} lịch` : null}
				extra={viewAllLink}
			/>

			<div style={{padding: '12px 18px'}}>
				{loading && (
					<div style={{textAlign: 'center', padding: '16px 0', color: '#94a3b8', fontSize: 13}}>
						<FontAwesomeIcon icon="fa-light fa-loader" spin style={{marginRight: 6}} />
						Đang tải...
					</div>
				)}

				{!loading && items.length === 0 && (
					<div style={{textAlign: 'center', padding: '20px 0', color: '#94a3b8'}}>
						<FontAwesomeIcon icon="fa-light fa-calendar-xmark" style={{fontSize: 26, display: 'block', marginBottom: 6}} />
						<span style={{fontSize: 12}}>Không có lịch đào tạo trong tuần này</span>
					</div>
				)}

				{!loading && items.map((item, index) => {
					const ds        = item.displayStatus || {};
					const cfg       = STATUS_MAP[ds.key] || {label: ds.label || item.status, color: 'default'};
					const startDay  = item.startTime ? dayjs.unix(item.startTime) : null;
					const endDay    = item.endTime   ? dayjs.unix(item.endTime)   : null;
					const dayLabel  = startDay ? startDay.format('ddd') : '';
					const dateLabel = startDay ? startDay.format('DD/MM') : '';
					const timeLabel = startDay && endDay ? `${startDay.format('HH:mm')} – ${endDay.format('HH:mm')}` : '';
					const trainer   = item.trainer ? `${item.trainer.firstname} ${item.trainer.lastname}` : '';
					const dotColor  = DOT_COLORS[index % DOT_COLORS.length];
					const isLast    = index === items.length - 1;
					const isToday   = startDay && startDay.isSame(dayjs(), 'day');

					return (
						<div key={item.id} style={{
							display: 'flex', gap: 10, alignItems: 'flex-start',
							paddingBottom: isLast ? 4 : 12, marginBottom: isLast ? 0 : 12,
							borderBottom: isLast ? 'none' : '1px dashed #e2e8f0',
							...(isToday && {
								background:   '#fef9ec',
								borderRadius: 10,
								padding:      '8px 10px',
								margin:       '0 -10px',
								borderBottom: isLast ? 'none' : '1px dashed #e2e8f0',
							}),
						}}>
							<div style={{
								minWidth: 46, textAlign: 'center', flexShrink: 0,
								background:   isToday ? dotColor        : `${dotColor}15`,
								border:       isToday ? `none`          : `1.5px solid ${dotColor}40`,
								borderRadius: 10, padding: '5px 4px',
								boxShadow:    isToday ? `0 2px 8px ${dotColor}60` : 'none',
							}}>
								<div style={{fontSize: 9, fontWeight: 700, color: isToday ? '#fff' : dotColor, textTransform: 'uppercase', letterSpacing: 0.5}}>{dayLabel}</div>
								<div style={{fontSize: 14, fontWeight: 800, color: isToday ? '#fff' : dotColor, lineHeight: 1.3}}>{dateLabel}</div>
							</div>

							<div style={{flex: 1, minWidth: 0}}>
								<div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4}}>
									{isToday && (
										<Tag color="volcano" style={{fontSize: 10, padding: '0 5px', lineHeight: '18px', margin: 0, flexShrink: 0}}>
											Hôm nay
										</Tag>
									)}
									<span style={{fontWeight: 600, fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
										{item.topic}
									</span>
								</div>
								<div style={{display: 'flex', flexWrap: 'wrap', gap: '3px 10px'}}>
									{timeLabel && (
										<span style={{fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 3}}>
											<FontAwesomeIcon icon="fa-light fa-clock" />{timeLabel}
										</span>
									)}
									{trainer && (
										<span style={{fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 3}}>
											<FontAwesomeIcon icon="fa-light fa-chalkboard-user" />{trainer}
										</span>
									)}
								</div>
							</div>

							<Tag color={cfg.color} style={{flexShrink: 0, fontSize: 11, margin: 0, alignSelf: 'center'}}>
								{cfg.label}
							</Tag>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default memo(TrainingScheduleWeek);
